import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import DropableColumn from '../components/DropabbleColumn';
import { QuantityGetDto } from 'svila-common';
import { WAREHOUSE_MOVEMENT_STATUS_TYPES, API_URL } from '~/src/utils/constants';
import ProductService from '~/src/services/logic/ProductService';
import { roundTo2digits } from '~/src/utils/misc';

interface ObjectType {
  index: number;
  palletsPerStatus: any;
  handleClick?: any;
  isCheck?: any;
}

export default function StatusComponent({
  productData,
  index,
  palletsPerStatus,
  handleClick,
  isCheck,
  getProducts,
}: ObjectType): JSX.Element {
  const [distinctBatches, setDistinctBatches] = useState<QuantityGetDto[]>(productData.quantities);
  const rowId = productData.product.genericProduct.id;

  useEffect(() => {
    const groupAndFilterBatches = (quantities) => {
      const grouped = quantities.reduce((acc, item) => {
        const { lot, status, expirationDate, fullDuUnits, singleCuUnits, singleDpUnits } = item;

        const key = `${lot}_${status}_${expirationDate}_${item.warehouseLocation.id}_${item.warehouse.id}_${item.purchaseOrder?.id}`;

        if (!acc[key]) {
          acc[key] = {
            ...item,
            fullDuUnits: 0,
            singleCuUnits: 0,
            singleDpUnits: 0,
          };
        }

        acc[key].fullDuUnits += fullDuUnits;
        acc[key].singleCuUnits += singleCuUnits;
        acc[key].singleDpUnits += singleDpUnits;

        return acc;
      }, {});

      const filtered = Object.values(grouped).filter(
        ({ fullDuUnits, singleCuUnits, singleDpUnits, purchaseOrder }) =>
          (fullDuUnits !== 0 || singleCuUnits !== 0 || singleDpUnits !== 0) &&
          purchaseOrder !== null
      );

      return filtered;
    };

    const distinctBatches = groupAndFilterBatches(productData.quantities);
    setDistinctBatches(distinctBatches);
  }, [productData]);

  return (
    <ColumnWrapper>
      <Row>
        <StyleCol>
          <>
            <p>
              {index}. {productData.product.genericProduct.name}
            </p>
            <p>{ProductService.getCu(productData.product.packagedProducts)?.gtin}</p>
          </>
        </StyleCol>
      </Row>

      <Row style={{ alignItems: 'stretch' }}>
        <DropableColumn
          columnName={WAREHOUSE_MOVEMENT_STATUS_TYPES.WAITING.value}
          distinctBatches={distinctBatches}
          setDistinctBatches={setDistinctBatches}
          rowId={rowId}
          title={'Waiting'}
          color={'#8F0CF5'}
          palletsPerStatus={roundTo2digits(palletsPerStatus?.waitingPallets ?? 0)}
          handleClick={handleClick}
          isChecked={isCheck}
          getProducts={getProducts}
        />

        <DropableColumn
          columnName={WAREHOUSE_MOVEMENT_STATUS_TYPES.LABELLING.value}
          distinctBatches={distinctBatches}
          setDistinctBatches={setDistinctBatches}
          rowId={rowId}
          title="Labeling"
          color="#EFBB03"
          palletsPerStatus={roundTo2digits(palletsPerStatus?.labellingPallets ?? 0)}
          handleClick={handleClick}
          isChecked={isCheck}
          getProducts={getProducts}
        />

        <DropableColumn
          columnName={WAREHOUSE_MOVEMENT_STATUS_TYPES.CONSOLIDATION.value}
          distinctBatches={distinctBatches}
          setDistinctBatches={setDistinctBatches}
          rowId={rowId}
          title="Consolidation"
          color="#0F6DF9"
          palletsPerStatus={roundTo2digits(palletsPerStatus?.consolidationPallets ?? 0)}
          handleClick={handleClick}
          isChecked={isCheck}
          getProducts={getProducts}
        />

        <DropableColumn
          columnName={WAREHOUSE_MOVEMENT_STATUS_TYPES.READY.value}
          distinctBatches={distinctBatches}
          setDistinctBatches={setDistinctBatches}
          rowId={rowId}
          title="Ready"
          color="#05B40C"
          palletsPerStatus={roundTo2digits(palletsPerStatus?.readyPallets ?? 0)}
          handleClick={handleClick}
          isChecked={isCheck}
          getProducts={getProducts}
        />
      </Row>
    </ColumnWrapper>
  );
}

const StyleCol = styled(Col)`
  background-color: #fff;
  color: #343434;
  font-size: 18px;
  font-weight: 700;
  padding: 5px;
  margin: 0 !important;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  p {
    margin-bottom: 0;
  }
`;

const ColumnWrapper = styled.div`
  margin-bottom: 35px;
`;
